import { portalFetch } from "@/lib/portalApi";
import { Button, ConfirmDialog, FormDialog, Input } from "@simplicity/ui";
import { Plus, Shield, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface SuperAdmin {
  userId: string;
  email: string;
  name: string | null;
  createdAt: string;
}

interface PromoteSuperAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPromoted: () => void;
  onError: (msg: string | null) => void;
}

function PromoteSuperAdminDialog({
  open,
  onOpenChange,
  onPromoted,
  onError,
}: PromoteSuperAdminDialogProps) {
  const [promoteEmail, setPromoteEmail] = useState("");
  const [promoting, setPromoting] = useState(false);

  // Reset the field each time the dialog opens.
  useEffect(() => {
    if (open) setPromoteEmail("");
  }, [open]);

  const handlePromote = async () => {
    const email = promoteEmail.trim().toLowerCase();
    if (!email) return;
    setPromoting(true);
    onError(null);
    try {
      await portalFetch("/api/portal/super-admins", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      onOpenChange(false);
      onPromoted();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to promote user.");
    }
    setPromoting(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Super Admin"
      description="Enter the email address of the user to promote. If they don't have an account yet, one will be created."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handlePromote();
            }}
            disabled={promoting || !promoteEmail.trim()}
          >
            {promoting ? "Promoting..." : "Promote"}
          </Button>
        </>
      }
    >
      <Input
        value={promoteEmail}
        onChange={(e) => setPromoteEmail(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void handlePromote();
          }
        }}
        placeholder="user@example.com"
        type="email"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- inside <FormDialog>; focus email field on open
        autoFocus
      />
    </FormDialog>
  );
}

function SuperAdminRow({ sa, onDemote }: { sa: SuperAdmin; onDemote: (sa: SuperAdmin) => void }) {
  return (
    <li className="flex items-center justify-between px-4 py-3 text-sm">
      <div className="flex items-center gap-3">
        <Shield className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{sa.email}</span>
        {sa.name && <span className="text-muted-foreground text-xs">{sa.name}</span>}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-auto w-auto p-1 text-muted-foreground hover:text-destructive"
        onClick={() => onDemote(sa)}
        title="Remove super admin access"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

interface SuperAdminsSectionProps {
  onError: (msg: string | null) => void;
}

export function SuperAdminsSection({ onError }: SuperAdminsSectionProps) {
  const [superAdmins, setSuperAdmins] = useState<SuperAdmin[]>([]);
  const [saLoading, setSaLoading] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  // Confirmation dialog (used for demote-super-admin only).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmDescription, setConfirmDescription] = useState("");

  const fetchSuperAdmins = useCallback(async () => {
    setSaLoading(true);
    try {
      const data = await portalFetch<SuperAdmin[]>("/api/portal/super-admins");
      setSuperAdmins(data);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to load super admins.");
    }
    setSaLoading(false);
  }, [onError]);

  useEffect(() => {
    void fetchSuperAdmins();
  }, [fetchSuperAdmins]);

  const handleDemote = (sa: SuperAdmin) => {
    setConfirmTitle("Remove Super Admin");
    setConfirmDescription(
      `Are you sure you want to remove super admin access from "${sa.email}"? They will retain any tenant-level roles.`,
    );
    setConfirmAction(() => async () => {
      onError(null);
      try {
        await portalFetch(`/api/portal/super-admins/${sa.userId}`, { method: "DELETE" });
        void fetchSuperAdmins();
      } catch (err: unknown) {
        onError(err instanceof Error ? err.message : "Failed to demote user.");
      }
    });
    setConfirmOpen(true);
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="h-5 w-5" /> Super Admins
        </h2>
        <Button size="sm" onClick={() => setPromoteOpen(true)}>
          <Plus className="h-4 w-4" /> Add Super Admin
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Super admins have full access across all tenants. Add by email address.
      </p>

      {saLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : superAdmins.length === 0 ? (
        <p className="text-sm text-muted-foreground">No super admins found.</p>
      ) : (
        <ul className="border border-border rounded divide-y divide-border">
          {superAdmins.map((sa) => (
            <SuperAdminRow key={sa.userId} sa={sa} onDemote={handleDemote} />
          ))}
        </ul>
      )}

      <PromoteSuperAdminDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        onPromoted={() => void fetchSuperAdmins()}
        onError={onError}
      />

      {/* Confirmation Alert Dialog (demote) */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmTitle}
        description={confirmDescription}
        onConfirm={() => confirmAction?.()}
        options={{ destructive: true }}
      />
    </section>
  );
}
